import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IBookingConfirmationEmail, defaultValue } from 'app/shared/model/booking-confirmation-email.model';

const initialState: EntityState<IBookingConfirmationEmail> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/bookingConfirmationEmails';

// Actions

export const getEntities = createAsyncThunk('bookingConfirmationEmail/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IBookingConfirmationEmail[]>(requestUrl);
});

export const getEntitiesByBankId = createAsyncThunk(
  'bookingConfirmationEmail/fetch_entity_list_by_bank_id',
  async (bankId: number | string) => {
    const requestUrl = `${apiUrl}/getEntitiesByBankId/${bankId}`;
    return axios.get<IBookingConfirmationEmail[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'bookingConfirmationEmail/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBookingConfirmationEmail>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'bookingConfirmationEmail/create_entity',
  async (entity: IBookingConfirmationEmail, thunkAPI) => {
    const result = await axios.post<IBookingConfirmationEmail>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'bookingConfirmationEmail/update_entity',
  async (entity: IBookingConfirmationEmail, thunkAPI) => {
    const result = await axios.put<IBookingConfirmationEmail>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'bookingConfirmationEmail/partial_update_entity',
  async (entity: IBookingConfirmationEmail, thunkAPI) => {
    const result = await axios.patch<IBookingConfirmationEmail>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'bookingConfirmationEmail/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBookingConfirmationEmail>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const BookingConfirmationEmailSlice = createEntitySlice({
  name: 'bookingConfirmationEmail',
  initialState,
  extraReducers(builder) {
    builder

      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntitiesByBankId), (state, action) => {
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntitiesByBankId, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BookingConfirmationEmailSlice.actions;

// Reducer
export default BookingConfirmationEmailSlice.reducer;
